const MARKETING_CONSENT_KEYS = ['newsletter', 'sms', 'voice', 'postal', 'social'];

export default {
  props: {
    formType: {
      type: String,
      default: '',
    },
    shouldHandleNewsletter: {
      type: Boolean,
      default: true,
    },
    initialValues: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: ['change', 'consent-values-loaded'],

  data() {
    return {
      forms: {},
      age: {
        id: `${this.formType}Age`,
        name: 'age',
        label: this.$t('Form.iAmOver14YearsOld'),
        type: 'checkbox',
        value: false,
      },
      basicFormTypes: ['newsletter', 'boutiqueAppointment', 'contact', 'accountCreation', 'openEvent', 'checkout'],
    };
  },

  computed: {
    currentForm() {
      return this.forms[this.formType] || null;
    },

    hasAcceptedEverything() {
      if (!this.displayGDPR || !this.currentForm) {
        return true;
      }

      const hasAcceptedRequiredBoxes = this.currentForm.consentBoxes.every(
        (consentBox) => !consentBox.required || consentBox.value === 'true',
      );
      const hasValidMarketingConsents = this.currentForm.consentBoxes.every(
        (consentBox) =>
          !consentBox.hasMarketingConsents || consentBox.value !== 'true' || consentBox.marketingConsents.length > 0,
      );

      return hasAcceptedRequiredBoxes && hasValidMarketingConsents && this.age.value;
    },

    displayGDPR() {
      return this.basicFormTypes.includes(this.formType);
    },

    consentPayload() {
      if (!this.currentForm) {
        return {
          newsletter: false,
          marketingConsent: false,
          marketingConsents: [],
        };
      }

      const marketingBox = this.currentForm.consentBoxes.find((consentBox) => consentBox.hasMarketingConsents);
      if (marketingBox) {
        const marketingConsent = marketingBox.value === 'true';
        const marketingConsents = marketingConsent ? [...marketingBox.marketingConsents] : [];

        return {
          newsletter: marketingConsents.includes('newsletter'),
          marketingConsent,
          marketingConsents,
        };
      }

      if (this.formType === 'newsletter') {
        const newsletterConsent = this.currentForm.consentBoxes[0]?.value === 'true';

        return {
          newsletter: newsletterConsent,
        };
      }

      const newsletterBox = this.currentForm.consentBoxes.find((consentBox) => consentBox.isNewsletterField === true);

      return {
        newsletter: newsletterBox ? newsletterBox.value === 'true' : false,
      };
    },
  },

  created() {
    this.initializeForms();
  },

  mounted() {
    if (this.formType === 'checkout') {
      this.loadConsentValues();
      return;
    }

    this.applyInitialMarketingConsents();
    this.emitChange();
  },

  watch: {
    'age.value'() {
      if (this.formType === 'checkout') {
        this.saveConsentValues();
      }

      this.emitChange();
    },
  },

  methods: {
    getPrivacyLink() {
      return `<a target='_blank' class='rcms_privacypol link link--text' href='${window.route(
        'kr_overseas_consent',
      )}'>${this.$t('Form.privacy')}</a>`;
    },

    createConsentOptions(prefix, dataCyPrefix = 'privacy') {
      const privacyMatch = dataCyPrefix.match(/^privacy(\d+)$/);
      const yesDataCy = privacyMatch ? `privacyYes${privacyMatch[1]}` : `${dataCyPrefix}Yes`;
      const noDataCy = privacyMatch ? `privacyNo${privacyMatch[1]}` : `${dataCyPrefix}No`;

      return [
        {
          id: `${prefix}Yes`,
          dataCy: yesDataCy,
          label: this.$t('Form.consent.agree'),
          value: 'true',
        },
        {
          id: `${prefix}No`,
          dataCy: noDataCy,
          label: this.$t('Form.consent.disagree'),
          value: 'false',
        },
      ];
    },

    createBasicConsentBox(type, index) {
      const boxId = `${type}ConsentBox${index}`;

      return {
        id: boxId,
        name: boxId,
        label: boxId,
        type: 'radio',
        required: true,
        value: '',
        options: this.createConsentOptions(`${type}Privacy${index}`, `privacy${index}`),
        texts: {
          title: this.$t(`Form.${boxId}.title`),
          header: this.$t(`Form.${boxId}.header`),
          items: this.$t(`Form.${boxId}.items`),
          purposes: this.$t(`Form.${boxId}.purposes`),
          retentionPeriod: this.$t(`Form.${boxId}.retentionPeriod`),
          footer: this.$t(`Form.${boxId}.footer`),
        },
      };
    },

    createMarketingConsentBox(type, options = {}) {
      const boxId = options.boxId ?? `${type}ConsentBoxMarketing`;

      return {
        id: boxId,
        name: boxId,
        label: boxId,
        type: 'radio',
        required: false,
        value: '',
        hasMarketingConsents: true,
        marketingConsents: [],
        options: this.createConsentOptions(`${boxId}Option`, options.dataCyPrefix ?? 'consentBoxMarketing'),
        texts: {
          title: this.$t(`Form.${boxId}.title`),
          header: this.$t(`Form.${boxId}.header`),
          items: this.$t(`Form.${boxId}.items`),
          purposes: this.$t(`Form.${boxId}.purposes`),
          retentionPeriod: this.$t(`Form.${boxId}.retentionPeriod`),
          footer: this.$t(`Form.${boxId}.footer`),
          subtitle: this.$t(`Form.${boxId}.subtitle`),
          label: this.$t(`Form.${boxId}.label`),
          channelsLabel: this.$t(`Form.${boxId}.channelsLabel`),
        },
      };
    },

    createLegacyNewsletterConsentBox(type) {
      return {
        id: `${type}ConsentBoxNewsletter`,
        name: `${type}ConsentBoxNewsletter`,
        label: `${type}ConsentBoxNewsletter`,
        isNewsletterField: true,
        type: 'radio',
        required: false,
        value: '',
        options: this.createConsentOptions('consentBoxNewsletter', 'consentBoxNewsletter'),
        texts: {
          title: this.$t(`Form.${type}ConsentBoxNewsletter.title`),
          header: this.$t(`Form.${type}ConsentBoxNewsletter.header`),
          items: this.$t(`Form.${type}ConsentBoxNewsletter.items`),
          purposes: this.$t(`Form.${type}ConsentBoxNewsletter.purposes`),
          retentionPeriod: this.$t(`Form.${type}ConsentBoxNewsletter.retentionPeriod`),
          footer: this.$t(`Form.${type}ConsentBoxNewsletter.footer`),
        },
      };
    },

    createCheckoutInformationalConsentBox() {
      return {
        id: 'checkoutConsentBox4',
        name: 'checkoutConsentBox4',
        required: false,
        value: 'true',
        texts: {
          title: this.$t('Form.checkoutConsentBox4.title'),
          subtitle: this.$t('Form.checkoutConsentBox4.subtitle'),
        },
      };
    },

    initializeForms() {
      const privacyLink = this.getPrivacyLink();

      this.forms = {
        newsletter: {
          consentBoxes: [this.createBasicConsentBox('newsletter', 1)],
          footer: this.$t('Form.newsletterConsent.footer', { privacyLink }),
          submitTexts: [this.$t('Form.newsletterConsent.submitText1'), this.$t('Form.newsletterConsent.submitText2')],
        },
        contact: {
          consentBoxes: [
            this.createBasicConsentBox('contact', 1),
            this.createMarketingConsentBox('contact', { boxId: 'contactConsentBoxNewsletter' }),
          ],
          footer: this.$t('Form.contactConsent.footer', { privacyLink }),
          submitTexts: [
            this.$t('Form.contactConsent.submitText1'),
            this.$t('Form.contactConsent.submitText2'),
            this.$t('Form.contactConsent.submitText3'),
          ],
        },
        boutiqueAppointment: {
          consentBoxes: [this.createBasicConsentBox('boutiqueAppointment', 1)],
          footer: this.$t('Form.boutiqueAppointmentConsent.footer', { privacyLink }),
          submitTexts: [
            this.$t('Form.boutiqueAppointmentConsent.submitText1'),
            this.$t('Form.boutiqueAppointmentConsent.submitText2'),
            this.$t('Form.boutiqueAppointmentConsent.submitText3'),
            this.$t('Form.boutiqueAppointmentConsent.submitText4'),
          ],
        },
        accountCreation: {
          consentBoxes: [
            this.createBasicConsentBox('accountCreation', 1),
            this.createMarketingConsentBox('accountCreation', { boxId: 'accountCreationConsentBoxNewsletter' }),
          ],
          footer: this.$t('Form.accountCreationConsent.footer', { privacyLink }),
          submitTexts: [
            this.$t('Form.accountCreationConsent.submitText1'),
            this.$t('Form.accountCreationConsent.submitText2'),
          ],
        },
        openEvent: {
          consentBoxes: [this.createBasicConsentBox('openEvent', 1)],
          footer: this.$t('Form.openEventConsent.footer', { privacyLink }),
          submitTexts: [
            this.$t('Form.openEventConsent.submitText1'),
            this.$t('Form.openEventConsent.submitText2'),
            this.$t('Form.openEventConsent.submitText3'),
          ],
        },
        checkout: {
          consentBoxes: [
            {
              id: 'checkoutConsentBox1',
              name: 'checkoutConsentBox1',
              label: 'checkoutConsentBox1',
              type: 'radio',
              required: true,
              value: '',
              options: this.createConsentOptions('checkoutPrivacy1', 'privacy1'),
              texts: {
                title: this.$t('Form.checkoutConsentBox1.title'),
                subtitle: this.$t('Form.checkoutConsentBox1.subtitle'),
                label: this.$t('Form.checkoutConsentBox1.label'),
              },
            },
            {
              id: 'checkoutConsentBox2',
              name: 'checkoutConsentBox2',
              label: 'checkoutConsentBox2',
              type: 'radio',
              required: true,
              value: '',
              options: this.createConsentOptions('checkoutPrivacy2', 'privacy2'),
              texts: {
                title: this.$t('Form.checkoutConsentBox2.title'),
                subtitle: this.$t('Form.checkoutConsentBox2.subtitle'),
                label: this.$t('Form.checkoutConsentBox2.label'),
              },
            },
            this.createMarketingConsentBox('checkout', {
              boxId: 'checkoutConsentBox3',
              dataCyPrefix: 'consentBoxMarketing',
            }),
            this.createCheckoutInformationalConsentBox(),
          ],
        },
      };

      if (this.shouldHandleNewsletter) {
        ['boutiqueAppointment', 'openEvent'].forEach((type) => {
          this.forms[type].consentBoxes.push(this.createLegacyNewsletterConsentBox(type));
        });
      }
    },

    emitChange() {
      this.$emit('change', this.hasAcceptedEverything, this.consentPayload);
    },

    applyInitialMarketingConsents() {
      if (!this.initialValues?.marketingConsent || !Array.isArray(this.initialValues?.marketingConsents)) {
        return;
      }

      const marketingBox = this.currentForm?.consentBoxes.find((cb) => cb.hasMarketingConsents);
      if (!marketingBox) {
        return;
      }

      marketingBox.value = 'true';
      marketingBox.marketingConsents = this.initialValues.marketingConsents.filter((c) =>
        MARKETING_CONSENT_KEYS.includes(c),
      );
    },

    updateConsentBoxValue(index, value) {
      const consentBox = this.forms[this.formType].consentBoxes[index];
      consentBox.value = value;

      if (consentBox.hasMarketingConsents && value !== 'true') {
        consentBox.marketingConsents = [];
      }

      if (this.formType === 'checkout') {
        this.saveConsentValues();
      }

      this.emitChange();
    },

    updateConsentBoxMarketingConsents(index, value) {
      this.forms[this.formType].consentBoxes[index].marketingConsents = value;

      if (this.formType === 'checkout') {
        this.saveConsentValues();
      }

      this.emitChange();
    },

    saveConsentValues() {
      const consentData = this.forms[this.formType].consentBoxes.reduce((acc, box) => {
        if (box.value === '') {
          return acc;
        }

        if (box.options) {
          acc[box.name] = box.value === 'true';
        }

        if (box.hasMarketingConsents) {
          acc.marketingConsent = box.value === 'true';
          acc.marketingConsents = box.value === 'true' ? [...box.marketingConsents] : [];
        }

        return acc;
      }, {});

      consentData.age = this.age.value;

      sessionStorage.setItem('userConsent', JSON.stringify(consentData));
    },

    getStoredConsentValues() {
      const saved = sessionStorage.getItem('userConsent');

      if (!saved) {
        return null;
      }

      try {
        const parsed = JSON.parse(saved);

        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
      } catch (err) {
        window.Phoenix.sentry.captureException(err);
        return null;
      }
    },

    applyCheckoutConsentValues(consent) {
      if (!consent || typeof consent !== 'object' || Array.isArray(consent)) {
        return;
      }

      this.forms.checkout.consentBoxes.forEach((consentBox) => {
        const storedValue = consent[consentBox.name];

        if (consentBox.hasMarketingConsents) {
          const marketingConsentValue =
            typeof consent.marketingConsent === 'boolean' ? consent.marketingConsent : storedValue;

          if (typeof marketingConsentValue === 'boolean') {
            consentBox.value = String(marketingConsentValue);
          }
        } else if (typeof storedValue === 'boolean') {
          consentBox.value = String(storedValue);
        }

        if (consentBox.hasMarketingConsents) {
          const storedMarketingConsents = Array.isArray(consent.marketingConsents)
            ? consent.marketingConsents.filter((marketingConsent) => MARKETING_CONSENT_KEYS.includes(marketingConsent))
            : [];

          consentBox.marketingConsents =
            storedMarketingConsents.length > 0 ? storedMarketingConsents : storedValue === true ? ['newsletter'] : [];
        }
      });

      if (typeof consent.age === 'boolean') {
        this.age.value = consent.age;
      }
    },

    loadConsentValues() {
      this.age.value = false;

      const savedConsent = this.getStoredConsentValues();
      if (savedConsent) {
        this.applyCheckoutConsentValues(savedConsent);
      } else {
        this.applyCheckoutConsentValues(this.initialValues);
      }

      this.emitChange();
      this.$emit('consent-values-loaded');
    },
  },
};
